import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Heart, Mail, Lock, Eye, EyeOff, ArrowRight } from "lucide-react";
import { signIn } from "../lib/authClient";

export function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const result = await signIn.email({ email, password });
    setSubmitting(false);

    if (result.error) {
      setError("E-Mail oder Passwort falsch.");
      return;
    }
    const next = searchParams.get("next") ?? "/deck";
    navigate(next, { replace: true });
  }

  return (
    <div className="relative min-h-screen bg-white text-ink-900">
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[460px] overflow-hidden">
        <div className="absolute -top-32 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-brand-200/50 blur-3xl" />
        <div className="absolute top-10 right-10 h-64 w-64 rounded-full bg-sunset-400/30 blur-3xl" />
        <div className="absolute top-20 left-10 h-64 w-64 rounded-full bg-brand-100 blur-3xl" />
      </div>

      <header className="container flex items-center justify-between py-5">
        <Link to="/" className="flex items-center gap-2.5">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-gradient shadow-glow">
            <Heart className="h-5 w-5 text-white" fill="currentColor" />
          </span>
          <span className="text-xl font-bold tracking-tight">Parble</span>
        </Link>
        <Link
          to="/register"
          className="text-sm font-semibold text-ink-500 hover:text-ink-900 transition"
        >
          Registrieren
        </Link>
      </header>

      <main className="container flex min-h-[calc(100vh-88px)] items-start justify-center py-8 md:items-center md:py-16">
        <div className="w-full max-w-md animate-fade-in-up">
          <div className="card p-8 md:p-10">
            <div className="text-center">
              <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-gradient shadow-glow">
                <Heart className="h-7 w-7 text-white" fill="currentColor" />
              </div>
              <h1 className="mt-5 text-3xl font-bold tracking-tight">
                Willkommen zurück
              </h1>
              <p className="mt-2 text-[15px] text-ink-500">
                Melde dich an, um deine Matches zu sehen.
              </p>
            </div>

            <form className="mt-8 space-y-4" onSubmit={handleSubmit}>
              {error && (
                <div
                  role="alert"
                  className="rounded-2xl border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-700"
                >
                  {error}
                </div>
              )}

              <div>
                <label
                  htmlFor="email"
                  className="mb-1.5 block text-sm font-semibold text-ink-700"
                >
                  E-Mail
                </label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-300" />
                  <input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="du@beispiel.de"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-field pl-11"
                  />
                </div>
              </div>

              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label
                    htmlFor="password"
                    className="text-sm font-semibold text-ink-700"
                  >
                    Passwort
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-xs font-semibold text-brand-500 hover:text-brand-600 transition"
                  >
                    Vergessen?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-300" />
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    required
                    autoComplete="current-password"
                    placeholder="Dein Passwort"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field pl-11 pr-11"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={
                      showPassword ? "Passwort verbergen" : "Passwort anzeigen"
                    }
                    className="absolute right-3 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-ink-500 hover:bg-surface hover:text-ink-900 transition"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting || !email || !password}
                className="btn-primary mt-2 w-full disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:shadow-glow"
              >
                {submitting ? "Anmelden …" : "Anmelden"}
                {!submitting && <ArrowRight className="h-4 w-4" />}
              </button>
            </form>

            <div className="my-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-widest text-ink-300">
              <span className="h-px flex-1 bg-border" />
              Oder
              <span className="h-px flex-1 bg-border" />
            </div>

            <p className="text-center text-sm text-ink-500">
              Noch kein Konto?{" "}
              <Link
                to="/register"
                className="font-semibold text-brand-500 hover:text-brand-600 transition"
              >
                Jetzt registrieren
              </Link>
            </p>
          </div>

          <p className="mt-6 text-center text-xs text-ink-300">
            Mit der Anmeldung akzeptierst du unsere{" "}
            <Link to="/legal/agb" className="underline hover:text-ink-500">
              AGB
            </Link>{" "}
            und{" "}
            <Link
              to="/legal/datenschutz"
              className="underline hover:text-ink-500"
            >
              Datenschutz
            </Link>
            .
          </p>
        </div>
      </main>
    </div>
  );
}
